import React, { useMemo } from 'react';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { DateTime } from 'luxon';

import Tooltip from '@material-ui/core/Tooltip';
import { Typography } from '@material-ui/core';

const useStyles = makeStyles(theme => ({
    container: {
        position: 'relative'
    },
    header: {
        height: 50,
        overflow: 'visible',
        zIndex: 10,
        position: 'relative',
        width: '100%',
    },
    timeline: {
        position: 'relative',
        height: 40,
        width: '100%',
        backgroundColor: theme.palette.grey[400],
    },
    timelineMaskTop: {
        position: 'absolute',
        left: 0,
        right: 0,
        top: 0,
        height: 18,
        zIndex: 5,
        backgroundColor: '#fff'
    },
    timelineMaskBottom: {
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 0,
        height: 18,
        zIndex: 5,
        backgroundColor: '#fff'
    },
    timelineRadius: {
        position: 'absolute',
        left: 0,
        right: 0,
        top: 18,
        height: 4,
        zIndex: 5,
        border: '5px solid #ffffff',
        borderRadius: 10,
        backgroundColor: '#fff'
    },
    timelineColor: {
        position: 'absolute',
        left: 0,
        bottom: 0,
        top: 0,
        zIndex: 4,
        backgroundColor: theme.palette.success.main
    },
    doughnut: {
        position: 'absolute',
        top: 8,
        border: '6px solid #fff',
        borderRadius: 12,
        width: 24,
        height: 24,
        zIndex: 8,
        cursor: 'pointer',
    },
    greyDoughnut: {
        position: 'absolute',
        top: 8,
        border: '6px solid #fff',
        borderRadius: 12,
        backgroundColor: theme.palette.grey[500],
        width: 24,
        height: 24,
        zIndex: 7
    },
    startDate: {
        position: 'absolute',
        top: 10,
        left: 0,
    },
    endDate: {
        position: 'absolute',
        top: 10,
        right: 0,
        textAlign: 'right'
    },
    currentDate: {
        position: 'absolute',
        top: 10,
        width: 160,
        textAlign: 'center'
    }
}));

export default function Timeline(props) {

    const { election } = props;

    const classes = useStyles();
    const theme = useTheme();

    const starts_at = election.starts_at;

    const ends_at = useMemo(() => {
        const endings = []

        if (election.proposals_end_at.isValid) endings.push(election.proposals_end_at);
        if (election.recommendations_end_at.isValid) endings.push(election.recommendations_end_at);
        if (election.voting_ends_at.isValid) endings.push(election.voting_ends_at);

        endings.sort((d1, d2) => d1 > d2 ? -1 : d1 < d2 ? 1 : 0);

        return endings[0] ?? null;
    }, [election]);

    const ttd = useMemo (() => ends_at?.diff(starts_at, 'minutes'), [ends_at]);

    const createHeader = variant => {
        // Get the position for today
        const ct = DateTime.local();
        const ctd = ct.diff(starts_at, 'minutes');
        const ctp = ((ctd.minutes / ttd.minutes) * 100).toFixed(2);

        return (
            <div className={classes.header}>
                <Typography
                    variant='body2'
                    color='textSecondary'
                    component="span"
                    className={classes.startDate}
                    >
                    Election Begins<br/>{starts_at.toLocaleString(DateTime.DATETIME_MED)}
                </Typography>
                {/*
                <Typography
                    variant='body2'
                    color='textSecondary'
                    className={classes.currentDate}
                    component="span"
                    style={{
                        left: `calc(${ctp}% - 80px)`
                    }}
                    >
                    Today<br/>{ct.toLocaleString(DateTime.DATETIME_MED)}
                </Typography>
                */}
                <Typography
                    variant='body2'
                    color='textSecondary'
                    component="span"
                    className={classes.endDate}
                    >
                    Election Ends<br/>{ends_at.toLocaleString(DateTime.DATETIME_MED)}
                </Typography>
            </div>
        )
    }

    const createTimeline = variant => {

        // Get the color for the variant
        const c = () => {
            switch (variant) {
                case 'proposal': return theme.palette.primary.main;
                case 'recommendation': return theme.palette.warning.main;
                case 'voting': return theme.palette.success.main;
            }
        }

        // Get the start and end positions from left side
        const s1 = (variant === 'voting') ? '' : 's';
        const s2 = (variant === 'voting') ? 's' : '';

        const st = election[`${variant}${s1}_start${s2}_at`];
        const et = election[`${variant}${s1}_end${s2}_at`];

        const std = st.diff(starts_at, 'minutes');
        const etd = et.diff(starts_at, 'minutes');

        const stp = ((std.minutes / ttd.minutes) * 100).toFixed(2);
        const etp = ((1 - (etd.minutes / ttd.minutes)) * 100).toFixed(2);

        // Get the position for today
        const ct = DateTime.local();
        const ctd = ct.diff(starts_at, 'minutes');
        const ctp = ((ctd.minutes / ttd.minutes) * 100).toFixed(2);

        return (
            <div className={classes.timeline}>
                <div className={classes.timelineMaskTop}/>
                <div className={classes.timelineMaskBottom}/>
                <div className={classes.timelineColor} style={{
                    left: stp + '%',
                    right: etp + '%',
                    backgroundColor: c()
                }}/>
                <Tooltip
                    title={
                        <Typography variant="caption" align='center'>
                            {`${window.ucFirst(variant)}${s1} Begin${s2}`}<br/>
                            {st.toLocaleString(DateTime.DATETIME_MED)}
                        </Typography>
                    }
                    aria-label="start time"
                    placement="top-start"
                    leaveDelay={1000}
                    arrow
                    >
                    <span className={classes.doughnut} style={{
                        backgroundColor: c(),
                        left: `calc(${stp}% - 12px)`
                    }}/>
                </Tooltip>
                <Tooltip
                    title={
                        <Typography variant="caption" align='center'>
                            {`${window.ucFirst(variant)}${s1} End${s2}`}<br/>
                            {et.toLocaleString(DateTime.DATETIME_MED)}
                        </Typography>
                    }
                    aria-label="end time"
                    placement="top-end"
                    leaveDelay={1000}
                    arrow
                    >
                    <span className={classes.doughnut} style={{
                        backgroundColor: c(),
                        right: `calc(${etp}% - 12px)`
                    }}/>
                </Tooltip>
                <Tooltip
                    title={
                        <Typography variant="caption" align='center'>
                            Today<br/>{ct.toLocaleString(DateTime.DATETIME_MED)}
                        </Typography>
                    }
                    aria-label="today"
                    placement="top"
                    leaveDelay={1000}
                    arrow
                    >
                    <span className={classes.greyDoughnut} style={{
                        left: `calc(${ctp}% - 12px)`
                    }}/>
                </Tooltip>

            </div>
        )
    }

    return (
        <div className={classes.container}>
            {ends_at && createHeader()}
            {election.proposals_start_at.isValid && createTimeline('proposal')}
            {election.recommendations_start_at.isValid && createTimeline('recommendation')}
            {election.voting_starts_at.isValid && createTimeline('voting')}
        </div>
    )
}
